import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for tracking user inactivity.
 *
 * @param {Object} config - Configuration object
 * @param {number} config.warningTime - Time in milliseconds before showing warning (default: 25 minutes)
 * @param {number} config.logoutTime - Time in milliseconds before auto-logout (default: 30 minutes)
 * @param {Function} config.onWarning - Callback when warning should be shown
 * @param {Function} config.onLogout - Callback when auto-logout should occur
 * @param {boolean} config.enabled - Whether the timer is enabled (default: true)
 *
 * @returns {Object} - { remainingTime, isWarning, resetTimer, pauseTimer, resumeTimer }
 */
export const useInactivityTimer = ({
  warningTime = 25 * 60 * 1000, // 25 minutes
  logoutTime = 30 * 60 * 1000,  // 30 minutes
  onWarning,
  onLogout,
  enabled = true
}) => {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const warningTriggeredRef = useRef(false);

  /**
   * Clear all timers
   */
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  /**
   * Start countdown when warning is shown
   */
  const startCountdown = useCallback(() => {
    const countdownDuration = logoutTime - warningTime; // 5 minutes
    const warningStartTime = Date.now();

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - warningStartTime;
      const remaining = Math.max(0, countdownDuration - elapsed);
      setRemainingTime(Math.ceil(remaining / 1000)); // Convert to seconds

      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, 1000);
  }, [logoutTime, warningTime]);

  /**
   * Reset the inactivity timer
   */
  const resetTimer = useCallback(() => {
    if (!enabled || isPaused) return;

    lastActivityRef.current = Date.now();
    warningTriggeredRef.current = false;
    setIsWarning(false);
    setRemainingTime(0);
    clearAllTimers();

    // Set warning timer (25 minutes)
    warningTimerRef.current = setTimeout(() => {
      if (!warningTriggeredRef.current) {
        warningTriggeredRef.current = true;
        setIsWarning(true);
        startCountdown();
        if (onWarning) onWarning();
      }
    }, warningTime);

    // Set logout timer (30 minutes)
    logoutTimerRef.current = setTimeout(() => {
      if (onLogout) onLogout();
    }, logoutTime);
  }, [enabled, isPaused, warningTime, logoutTime, onWarning, onLogout, clearAllTimers, startCountdown]);

  /**
   * Pause the timer (used when modal is open)
   */
  const pauseTimer = useCallback(() => {
    setIsPaused(true);
  }, []);

  /**
   * Resume the timer
   */
  const resumeTimer = useCallback(() => {
    setIsPaused(false);
    resetTimer();
  }, [resetTimer]);

  /**
   * Handle user activity
   */
  const handleActivity = useCallback(() => {
    if (!enabled || isPaused) return;

    // If warning is showing and user interacts, close warning and reset
    if (isWarning) {
      setIsWarning(false);
      setRemainingTime(0);
      clearAllTimers();
    }

    resetTimer();
  }, [enabled, isPaused, isWarning, resetTimer, clearAllTimers]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    // Throttle activity handler to avoid excessive resets
    let throttleTimeout = null;
    const throttledHandler = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        handleActivity();
        throttleTimeout = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledHandler);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
      clearAllTimers();
    };
  }, [enabled, handleActivity, resetTimer, clearAllTimers]);

  return {
    remainingTime,
    isWarning,
    resetTimer,
    pauseTimer,
    resumeTimer
  };
};
